import { useEffect, useState } from 'react';
import { DataTable, InlineGrid, Link, Modal, SkeletonBodyText, Text } from '@shopify/polaris';
import { formatToCurrency, getRequest } from '../../../../shared/components/functions';
import { formatDrillPeriodLabel } from './PeriodDrilldownModal';
import { formatInteger, formatPercent, periodToQueryParams, sumBy, toNumber } from './analyticsHelpers';

const TOP_ROWS = 15;

function SideTable({ title, rows, entityHeading }) {
  const total = sumBy(rows, 'total_incl');
  const tableRows = rows.slice(0, TOP_ROWS).map((row, idx) => [
    idx + 1,
    <Link removeUnderline url={`/companies/${row.company_id}`}>
      {String(row.company_name || 'Unknown').substring(0, 30)}
    </Link>,
    formatInteger(row.invoice_count),
    formatToCurrency(toNumber(row.total_incl)),
    total ? formatPercent(toNumber(row.total_incl) / total) : '—'
  ]);
  return (
    <div>
      <Text variant="headingSm" as="h3">
        {title}
      </Text>
      <Text tone="subdued" as="p" variant="bodySm">
        {formatInteger(rows.length)} {entityHeading.toLowerCase()}s · {formatToCurrency(total)} incl. VAT{rows.length > TOP_ROWS ? ` · top ${TOP_ROWS} shown` : ''}
      </Text>
      <DataTable
        verticalAlign="middle"
        increasedTableDensity
        columnContentTypes={['numeric', 'text', 'numeric', 'numeric', 'numeric']}
        headings={[<b>#</b>, <b>{entityHeading}</b>, <b>Inv.</b>, <b>Total</b>, <b>Share</b>]}
        rows={tableRows}
      />
    </div>
  );
}

// Margin drill-down: revenue and costs for the clicked period side by side,
// so a good or bad margin period can be explained in one view.
export default function MarginDrilldownModal({ period, onClose, businessUnitId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!period) return;
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      setData(null);
      const params = periodToQueryParams(period);
      if (businessUnitId) params.append('businessUnitId', businessUnitId);
      const [revenue, costs] = await Promise.all([getRequest(`/api/metrics/revenue-period-detail?${params}`), getRequest(`/api/metrics/costs-period-detail?${params}`)]);
      if (!cancelled) {
        setData({ revenue, costs });
        setLoading(false);
      }
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [period, businessUnitId]);

  const revenueCompanies = Array.isArray(data?.revenue?.byCompany) ? data.revenue.byCompany : [];
  const costCompanies = Array.isArray(data?.costs?.byCompany) ? data.costs.byCompany : [];
  const revenueTotal = sumBy(revenueCompanies, 'total_incl');
  const costsTotal = sumBy(costCompanies, 'total_incl');
  const balance = revenueTotal - costsTotal;

  return (
    <Modal size="large" open={!!period} onClose={onClose} title={`Margin — ${formatDrillPeriodLabel(period)}`}>
      <Modal.Section>
        {loading || !data ? (
          <SkeletonBodyText lines={10} />
        ) : (
          <>
            <Text as="p">
              Invoiced out {formatToCurrency(revenueTotal)} · booked in {formatToCurrency(costsTotal)} · balance{' '}
              <Text tone={balance >= 0 ? 'success' : 'critical'} as="span" fontWeight="semibold">
                {formatToCurrency(balance)}
              </Text>{' '}
              (all incl. VAT — the heatmap cell itself is excl. VAT)
            </Text>
            {toNumber(data.revenue?.creditNotes?.credit_note_count) > 0 || toNumber(data.costs?.creditNotes?.credit_note_count) > 0 ? (
              <Text tone="subdued" as="p" variant="bodySm">
                Credit notes dated in this period — outgoing: {formatInteger(data.revenue?.creditNotes?.credit_note_count)} (−
                {formatToCurrency(toNumber(data.revenue?.creditNotes?.total_incl))}) · incoming: {formatInteger(data.costs?.creditNotes?.credit_note_count)} (−
                {formatToCurrency(toNumber(data.costs?.creditNotes?.total_incl))}). The tables below show gross invoice amounts; the heatmap cell is net of these.
              </Text>
            ) : null}
            <div style={{ marginTop: '16px' }}>
              <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                <SideTable title="Revenue — top customers" rows={revenueCompanies} entityHeading="Customer" />
                <SideTable title="Costs — top suppliers" rows={costCompanies} entityHeading="Supplier" />
              </InlineGrid>
            </div>
          </>
        )}
      </Modal.Section>
    </Modal>
  );
}
